import React, { Children, useCallback, useState } from "react";
import styled from "styled-components";
import { Icon as IconButton } from "@churchofjesuschrist/eden-buttons";
import { GridView, Clear, ArrowBack, ChevronLeftLarge, ChevronRightLarge } from "@churchofjesuschrist/eden-icons";
import { spacing8, spacing24, grey90 } from "@churchofjesuschrist/eden-style-constants";
import { mediaBreak600w, dir } from "@churchofjesuschrist/eden-styled-helpers";
import { Text4 } from "@churchofjesuschrist/eden-text";
import Caption from "./Caption";
import Grid from "./Grid";
import ItemLayout from "./ItemLayout";
import Head from "./Head";
import { AppearanceSection } from "@churchofjesuschrist/eden-appearance";
import { adjustHexAlpha } from "@churchofjesuschrist/eden-utilities";
import PlatformModal from "@churchofjesuschrist/eden-platform-modal";
import HotKey from "@churchofjesuschrist/eden-hotkey";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const Dialog = styled(PlatformModal).withConfig({
  componentId: "sc-1xrexcs-0"
})(["color:inherit;border:none;width:100vw;height:100vh;max-height:100dvh;max-width:100dvw;backdrop-filter:blur(1px);background:", ";&::backdrop{display:none;}"], adjustHexAlpha(grey90, 0.87));
const Container = styled.div.withConfig({
  componentId: "sc-1xrexcs-1"
})(["display:grid;grid-template-columns:1fr;width:100vw;height:100vh;max-height:100dvh;max-width:100dvw;overflow:auto;"]);
const PreviousNextButton = styled(IconButton).withConfig({
  componentId: "sc-1xrexcs-2"
})(["margin:0 ", ";"], spacing24);
const BackButton = styled(IconButton).withConfig({
  componentId: "sc-1xrexcs-3"
})(["", ""], dir("rtl")`
    transform: rotate(180deg);
  `);
const HeadPiece = styled.div.withConfig({
  componentId: "sc-1xrexcs-4"
})(["display:flex;align-items:center;"]);
const HeadNav = styled.div.withConfig({
  componentId: "sc-1xrexcs-5"
})(["display:flex;", ";"], mediaBreak600w`
    display: none;
  `);

/**
 * Displays a full-screen modal, obscuring the background and displaying media above the page content.
 * @param {object} props - The component props
 * @param {boolean} [props.open] - Whether modal is open
 * @param {Function} props.onClose - Callback to execute when a close method (X, Esc key) is triggered
 * @param {React.ReactNode} props.children - Used to define what the media items in the modal are. Should be one or more of ModalImage and ModalVideo
 * @param {number} [props.initialItemIndex] - Defines which item in the gallery should be active when it first opens
 * @param {string} [props.gridViewLabel] - Label for the "grid view" control
 * @param {string} [props.backLabel] - Label for the "back" control
 * @param {string} [props.previousLabel] - Label for the "previous" control
 * @param {string} [props.nextLabel] - Label for the "next" control
 * @param {string} [props.closeLabel] - Label for the "close" control
 * @returns {React.ReactNode} - The MediaModal component.
 */
const MediaModal = ({
  open,
  onClose,
  children,
  initialItemIndex = 0,
  gridViewLabel = "Grid View",
  backLabel = "Back",
  previousLabel = "Previous",
  nextLabel = "Next",
  closeLabel = "Close",
  ...props
}) => {
  const items = Children.toArray(children);
  const [viewMode, setViewMode] = useState("item");
  const [currentItemIndex, setCurrentItemIndex] = useState(initialItemIndex);
  const goToGrid = () => {
    setViewMode("grid");
  };
  const goToItem = () => {
    setViewMode("item");
  };
  const handleItemSelected = index => {
    setCurrentItemIndex(index);
    setViewMode("item");
  };
  const handleClose = event => {
    onClose(event);
  };
  const handlePrevious = useCallback(() => {
    const itemsCount = items.length;
    if (viewMode !== "item" || !open) {
      return;
    }
    if (currentItemIndex === 0) {
      setCurrentItemIndex(itemsCount - 1);
    } else {
      setCurrentItemIndex(prevIndex => prevIndex - 1);
    }
  }, [currentItemIndex, items.length, open, viewMode]);
  const handleNext = useCallback(() => {
    const itemsCount = items.length;
    if (viewMode !== "item" || !open) {
      return;
    }
    if (currentItemIndex >= itemsCount - 1) {
      setCurrentItemIndex(0);
    } else {
      setCurrentItemIndex(prevIndex => prevIndex + 1);
    }
  }, [currentItemIndex, items.length, open, viewMode]);
  const currentItem = items[currentItemIndex];
  let previous = null;
  let next = null;
  if (items.length > 1) {
    previous = /*#__PURE__*/_jsx(PreviousNextButton, {
      onClick: handlePrevious,
      title: previousLabel,
      children: /*#__PURE__*/_jsx(ChevronLeftLarge, {})
    });
    next = /*#__PURE__*/_jsx(PreviousNextButton, {
      onClick: handleNext,
      title: nextLabel,
      children: /*#__PURE__*/_jsx(ChevronRightLarge, {})
    });
  }
  return /*#__PURE__*/_jsxs(Dialog, {
    open: open,
    onClose: handleClose,
    ...props,
    children: [/*#__PURE__*/_jsx(HotKey, {
      sequence: ["ArrowLeft"],
      onMatch: handlePrevious
    }), /*#__PURE__*/_jsx(HotKey, {
      sequence: ["ArrowRight"],
      onMatch: handleNext
    }), /*#__PURE__*/_jsx(AppearanceSection, {
      mode: "dark",
      style: {
        "--background-color-level-current": "transparent"
      },
      children: open && /*#__PURE__*/_jsx(Container, {
        children: viewMode === "item" ? /*#__PURE__*/_jsx(ItemLayout, {
          head: /*#__PURE__*/_jsx(Head, {
            start: items.length > 1 ? /*#__PURE__*/_jsx(HeadPiece, {
              children: /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx(IconButton, {
                  onClick: goToGrid,
                  title: gridViewLabel,
                  children: /*#__PURE__*/_jsx(GridView, {})
                }), /*#__PURE__*/_jsxs(Text4, {
                  style: {
                    paddingLeft: spacing8,
                    paddingRight: spacing8
                  },
                  children: [currentItemIndex + 1, " / ", items.length]
                })]
              })
            }) : null,
            end: /*#__PURE__*/_jsxs(HeadPiece, {
              children: [/*#__PURE__*/_jsxs(HeadNav, {
                children: [previous, next]
              }), /*#__PURE__*/_jsx(IconButton, {
                onClick: handleClose,
                "data-testid": "closeButton",
                title: closeLabel,
                children: /*#__PURE__*/_jsx(Clear, {})
              })]
            })
          }),
          previous: previous,
          next: next,
          main: currentItem,
          foot: /*#__PURE__*/_jsx(Caption, {
            id: currentItem.props["aria-describedby"],
            title: currentItem.props.title,
            caption: currentItem.props.caption,
            attribution: currentItem.props.attribution,
            tools: currentItem.props.tools
          })
        }) : /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx(Head, {
            start: /*#__PURE__*/_jsx(HeadPiece, {
              children: /*#__PURE__*/_jsx(BackButton, {
                onClick: goToItem,
                title: backLabel,
                children: /*#__PURE__*/_jsx(ArrowBack, {})
              })
            }),
            end: /*#__PURE__*/_jsx(HeadPiece, {
              children: /*#__PURE__*/_jsx(IconButton, {
                onClick: handleClose,
                "data-testid": "closeButton",
                title: closeLabel,
                children: /*#__PURE__*/_jsx(Clear, {})
              })
            })
          }), /*#__PURE__*/_jsx(Grid, {
            items: items,
            activeIndex: currentItemIndex,
            onItemSelected: handleItemSelected
          })]
        })
      })
    })]
  });
};
MediaModal.displayName = "MediaModal";
export default MediaModal;